/**
 * LP 設定の Context。
 *
 * LP の部品（routes/LandingPage/components/Lp*.tsx）は設定を直接 import せず、ここの hook で読む。
 * 検索 LP は routes/LandingPage/index.tsx、配信 LP は routes/LandingPageDelivery/index.tsx が
 * それぞれの設定で Provider を張る。Provider は DOM を足さない。
 */
import { createContext, type ReactNode, useContext } from "react";
import type {
  LpDeliveryConfig,
  LpSearchConfig,
  LpSharedConfig,
} from "routes/LandingPage/lpConfig";

/** どの LP を描画しているか */
export type LpPage = "search" | "delivery";

/** Provider に渡す値。`page` で設定の型が決まる */
export type LpConfigContextValue =
  | { page: "search"; config: LpSearchConfig }
  | { page: "delivery"; config: LpDeliveryConfig };

const LpConfigContext = createContext<LpConfigContextValue | null>(null);

/**
 * LP 設定を子孫に配る Provider。DOM は足さない。
 * @param {object} props props
 * @param {LpConfigContextValue} props.value 描画する LP と、その設定
 * @param {ReactNode} props.children 子要素
 * @return {JSX.Element} Provider
 */
export function LpConfigProvider(props: {
  value: LpConfigContextValue;
  children: ReactNode;
}): JSX.Element {
  return (
    <LpConfigContext.Provider value={props.value}>
      {props.children}
    </LpConfigContext.Provider>
  );
}

/**
 * Context の値を読む。Provider の外なら Error を投げる。
 * @return {LpConfigContextValue} Context の値
 */
function useLpConfigContextValue(): LpConfigContextValue {
  const value = useContext(LpConfigContext);
  if (value === null) {
    throw new Error("LP の部品は LpConfigProvider の中で描画してください");
  }
  return value;
}

/**
 * 両 LP が共通に持つ節を読む。Provider の外なら Error を投げる。
 * @return {LpSharedConfig} 共通の LP 設定
 */
export function useLpConfig(): LpSharedConfig {
  return useLpConfigContextValue().config;
}

/**
 * 検索 LP の設定を読む（検索 LP だけの部品が使う）。検索 LP 以外では Error を投げる。
 * @return {LpSearchConfig} 検索 LP の設定
 */
export function useLpSearchConfig(): LpSearchConfig {
  const value = useLpConfigContextValue();
  if (value.page !== "search") {
    throw new Error(`検索 LP だけの部品が ${value.page} LP で描画されました`);
  }
  return value.config;
}

/**
 * 描画中の LP を返す（問い合わせの source などに使う）。
 * @return {LpPage} 描画中の LP
 */
export function useLpPage(): LpPage {
  return useLpConfigContextValue().page;
}
